import {
  EMAIL_REGEX,
  URL_REGEX,
  isValidPort,
  parseJsonSafe,
  validationResult,
} from "./patterns";
import {
  FIELD_LIMITS,
  optionalSafeTextField,
  runFieldValidation,
  validateName,
} from "./yupCommonFields";

/** @returns {{ valid: boolean, message?: string }} */
export function validateIntegrationField(field, value, { isEdit = false } = {}) {
  if (!field) return validationResult(true);

  const type = field.type || "text";
  const label = field.label || field.key || "Field";
  const raw = value === undefined || value === null ? "" : value;

  if (field.required) {
    if (type === "secret" && isEdit) {
      // blank secret on edit = keep existing
    } else if (type === "tags") {
      const arr = Array.isArray(raw) ? raw : [];
      if (arr.length === 0) {
        return validationResult(false, `${label} is required`);
      }
    } else if (type === "bool") {
      // bool always has value
    } else if (typeof raw === "string" && raw.trim().length === 0) {
      return validationResult(false, `${label} is required`);
    } else if (raw === "" || raw === null || raw === undefined) {
      return validationResult(false, `${label} is required`);
    }
  }

  if (type === "secret" && isEdit && (raw === "" || raw === null || raw === undefined)) {
    return validationResult(true);
  }

  switch (type) {
    case "url": {
      const s = String(raw).trim();
      if (!s) return field.required ? validationResult(false, `${label} is required`) : validationResult(true);
      if (!URL_REGEX.test(s)) {
        return validationResult(false, "Enter a valid URL starting with http:// or https://");
      }
      return validationResult(true);
    }

    case "secret": {
      const s = String(raw).trim();
      if (!s && field.required) return validationResult(false, `${label} is required`);
      if (s && (field.key === "webhook_url" || label.toLowerCase().includes("webhook url"))) {
        if (!URL_REGEX.test(s)) {
          return validationResult(false, "Enter a valid webhook URL starting with http:// or https://");
        }
      }
      if (s && field.key === "routing_key" && !/^[0-9a-fA-F]{32}$/.test(s)) {
        return validationResult(false, "Routing key must be 32 hexadecimal characters");
      }
      return validationResult(true);
    }

    case "email": {
      const s = String(raw).trim();
      if (!s) return field.required ? validationResult(false, `${label} is required`) : validationResult(true);
      if (!EMAIL_REGEX.test(s)) return validationResult(false, "Enter a valid email address");
      return validationResult(true);
    }

    case "number": {
      const s = String(raw).trim();
      if (!s) return field.required ? validationResult(false, `${label} is required`) : validationResult(true);
      const n = Number(s);
      if (!Number.isFinite(n)) return validationResult(false, `${label} must be a number`);
      if (field.min !== undefined && n < field.min) {
        return validationResult(false, `${label} must be at least ${field.min}`);
      }
      if (field.max !== undefined && n > field.max) {
        return validationResult(false, `${label} must be at most ${field.max}`);
      }
      return validationResult(true);
    }

    case "json": {
      const s = typeof raw === "string" ? raw : JSON.stringify(raw);
      if (!s || s === "" || s === "{}" || s === "[]") {
        return field.required ? validationResult(false, `${label} is required`) : validationResult(true);
      }
      const parsed = parseJsonSafe(s);
      if (!parsed.ok) {
        return validationResult(false, `${label}: invalid JSON (${parsed.message})`);
      }
      return validationResult(true);
    }

    case "tags": {
      const arr = Array.isArray(raw) ? raw : [];
      if (field.required && arr.length === 0) {
        return validationResult(false, `${label} requires at least one value`);
      }
      return validationResult(true);
    }

    case "text":
    case "textarea":
    default: {
      const maxLen = field.maxLength || FIELD_LIMITS.description;
      return runFieldValidation(optionalSafeTextField(maxLen, label), raw);
    }
  }
}

export function validateDestinationForm(meta, edit, { isEdit = false } = {}) {
  const fieldErrors = {};
  const formErrors = {};

  const name = String(edit?.name ?? "").trim();
  if (name.length < 2) formErrors.name = "Name must be at least 2 characters";
  else if (name.length > 256) formErrors.name = "Name must be 256 characters or fewer";
  else {
    const nameResult = validateName(name);
    if (!nameResult.valid) formErrors.name = nameResult.message;
  }

  for (const f of meta?.fields || []) {
    const result = validateIntegrationField(f, edit?.config?.[f.key], { isEdit });
    if (!result.valid) fieldErrors[f.key] = result.message;
  }

  const valid = Object.keys(formErrors).length === 0 && Object.keys(fieldErrors).length === 0;
  return { valid, formErrors, fieldErrors };
}
